import React, { useState, useEffect } from "react";
import axios from "axios";

import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import InfoIcon from "@mui/icons-material/Info";
import ListAltIcon from "@mui/icons-material/ListAlt";
import VerifiedIcon from "@mui/icons-material/Verified";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

const API_URL = process.env.REACT_APP_API_URL || "https://api.quest-connect.com/api";

// Axios interceptor to add auth token to all requests
// axios.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem("authToken");
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );



function AdminInterviewRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [grantingId, setGrantingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [grantedRequests, setGrantedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [rejectComment, setRejectComment] = useState("");
  const [adminComment, setAdminComment] = useState(""); // New state for admin comment
  const [showRejectInput, setShowRejectInput] = useState(null);
  const [showGrantInput, setShowGrantInput] = useState(null); // New state for grant input
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/profile-update-requests`,
        {
          withCredentials: true, // ✅ IMPORTANT
        }
      );

      setRequests(response.data.requests || []);
    } catch (error) {
      console.error("Failed to fetch requests", error);
      handleApiError(error, "Failed to load requests. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGrantPermission = async (id, userComment = "") => {
    if (!adminComment.trim() && !userComment.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide an admin comment.",
        color: "warning",
      });
      return;
    }

    setGrantingId(id);
    try {
      const finalAdminComment = adminComment.trim() || "Permission granted by admin";

      // await axios.post(`${API_URL}/admin/profile-update-request/grant`, {
      //   requestId: id,
      //   comment: `${finalAdminComment}. User comment: ${userComment || "No user comment"}`,
      //   admin_comment: finalAdminComment // Send admin_comment separately
      // });

      await axios.post(
        `${API_URL}/admin/profile-update-request/grant`,
        {
          requestId: id,
          comment: `${finalAdminComment}. User comment: ${userComment || "No user comment"}`,
          admin_comment: finalAdminComment,
        },
        {
          withCredentials: true, // ✅ IMPORTANT
        }
      );


      setGrantedRequests((prev) => [...prev, id]);
      setRequests((prev) => prev.filter((request) => request.id !== id));
      setAdminComment("");
      setShowGrantInput(null);

      setSnackbar({
        open: true,
        message: "Permission granted successfully!",
        color: "success",
      });
    } catch (error) {
      console.error(error);
      handleApiError(error, "Failed to grant permission. Please try again.");
    } finally {
      setGrantingId(null);
    }
  };

  const handleRejectRequest = async (id, userComment = "") => {
    if (!rejectComment.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a rejection comment.",
        color: "warning",
      });
      return;
    }

    setRejectingId(id);
    try {
      // await axios.post(`${API_URL}/admin/profile-update-request/reject`, {
      //   requestId: id,
      //   comment: `Rejection reason: ${rejectComment}. User comment: ${userComment || "No user comment"}`,
      //   admin_comment: rejectComment // Send admin_comment for rejection
      // });

      await axios.post(
        `${API_URL}/admin/profile-update-request/reject`,
        {
          requestId: id,
          comment: `Rejection reason: ${rejectComment}. User comment: ${userComment || "No user comment"}`,
          admin_comment: rejectComment,
        },
        {
          withCredentials: true, // ✅ IMPORTANT
        }
      );


      setRejectedRequests((prev) => [...prev, id]);
      setRequests((prev) => prev.filter((request) => request.id !== id));
      setRejectComment("");
      setShowRejectInput(null);

      setSnackbar({
        open: true,
        message: "Request rejected successfully!",
        color: "success",
      });
    } catch (error) {
      console.error(error);
      handleApiError(error, "Failed to reject request. Please try again.");
    } finally {
      setRejectingId(null);
    }
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 401) {
        setSnackbar({
          open: true,
          message: "Authentication failed. Please login again.",
          color: "error",
        });
      } else if (error.response.status === 403) {
        setSnackbar({
          open: true,
          message: "You don't have permission to perform this action.",
          color: "error",
        });
      } else if (error.response.status === 404) {
        setSnackbar({
          open: true,
          message: "Resource not found.",
          color: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: error.response.data.message || defaultMessage,
          color: "error",
        });
      }
    } else if (error.request) {
      // Network error
      setSnackbar({
        open: true,
        message: "Network error. Please check your connection.",
        color: "error",
      });
    } else {
      // Other errors
      setSnackbar({
        open: true,
        message: defaultMessage,
        color: "error",
      });
    }
  };

  const cancelReject = () => {
    setRejectComment("");
    setShowRejectInput(null);
  };

  const cancelGrant = () => {
    setAdminComment("");
    setShowGrantInput(null);
  };

  const getAuthToken = () => {
    return localStorage.getItem("authToken");
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = getAuthToken();
    return !!token;
  };

  if (!isAuthenticated()) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox my={3}>
          <Card>
            <SoftBox p={3} textAlign="center">
              <SoftTypography variant="h6" color="error">
                Authentication Required
              </SoftTypography>
              <SoftTypography variant="body2" color="text.secondary">
                Please login to access this page.
              </SoftTypography>
            </SoftBox>
          </Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card>
          <SoftBox p={3}>
            {/* Title */}
            <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
              <ListAltIcon color="dark" />
              <Typography variant="h6" fontWeight="bold">
                Pending Profile Update Requests
              </Typography>
            </SoftBox>

            {/* Info Message */}
            <SoftBox
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              bgcolor="#f0f4f8"
              borderRadius="8px"
              mb={3}
            >
              <InfoIcon color="action" />
              <SoftTypography variant="body2" color="text.secondary">
                Review and grant or reject permission for employees to update specific sections.
                Once granted, they will have access to edit those fields.
              </SoftTypography>
            </SoftBox>

            {/* Authentication Status */}
            <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
              <VerifiedIcon color="success" fontSize="small" />
              <SoftTypography variant="caption" color="success.main">
                Authenticated as Admin
              </SoftTypography>
            </SoftBox>

            {loading ? (
              <SoftBox display="flex" justifyContent="center" alignItems="center" py={3}>
                <CircularProgress size={24} />
                <SoftTypography variant="body2" color="text.secondary" ml={2}>
                  Loading requests...
                </SoftTypography>
              </SoftBox>
            ) : requests.length === 0 ? (
              <SoftTypography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No pending requests.
              </SoftTypography>
            ) : (
              requests.map((request) => {
                const isGranted = grantedRequests.includes(request.id);
                const isRejected = rejectedRequests.includes(request.id);
                const isShowingRejectInput = showRejectInput === request.id;
                const isShowingGrantInput = showGrantInput === request.id;

                if (isGranted || isRejected) return null;

                return (
                  <SoftBox
                    key={request.id}
                    display="flex"
                    flexDirection="column"
                    borderBottom="1px solid #eee"
                    py={2}
                    gap={1}
                  >
                    <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                      <SoftBox flex={1}>
                        {/* <SoftTypography variant="subtitle2" fontWeight="bold">
                          {request.user}
                        </SoftTypography> */}
                        <SoftTypography variant="subtitle2" fontWeight="bold">
                          {request.user?.name}
                        </SoftTypography>

                        {/* 🔹 Branch */}
                        {request.user?.branch && (
                          <SoftTypography variant="caption" color="text.secondary" display="block">
                            Branch: {request.user.branch}
                          </SoftTypography>
                        )}

                        {/* 🔹 Shift Details */}
                        {request.shift && (
                          <SoftTypography variant="caption" color="text.secondary" display="block">
                            Shift: {request.shift.type} ({request.shift.start_time} - {request.shift.end_time})
                          </SoftTypography>
                        )}
                        <SoftTypography variant="caption" color="text.secondary" mb={0.5}>
                          Requested Sections: {request.sections.join(", ")}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text.secondary" display="block">
                          Request ID: {request.id}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text.secondary" display="block">
                          Status: {request.status || "pending"}
                        </SoftTypography>
                        {/* <SoftTypography variant="caption" color="text.secondary" display="block">
                          Created: {new Date(request.created_at).toLocaleDateString()}
                        </SoftTypography> */}
                      </SoftBox>

                      {/* Action Buttons */}
                      <SoftBox display="flex" alignItems="center" gap={1}>
                        {/* Grant Button */}
                        {!isShowingGrantInput ? (
                          <SoftButton
                            color="success"
                            size="small"
                            iconOnly
                            circular
                            onClick={() => setShowGrantInput(request.id)}
                            disabled={grantingId === request.id || rejectingId === request.id}
                          >
                            {grantingId === request.id ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <CheckCircleIcon />
                            )}
                          </SoftButton>
                        ) : (
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftButton
                              color="success"
                              size="small"
                              onClick={() => handleGrantPermission(request.id, request.comment || "")}
                              disabled={grantingId === request.id}
                            >
                              {grantingId === request.id ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                "Confirm Grant"
                              )}
                            </SoftButton>
                            <SoftButton
                              color="dark"
                              size="small"
                              variant="outlined"
                              onClick={cancelGrant}
                            >
                              Cancel
                            </SoftButton>
                          </SoftBox>
                        )}

                        {/* Reject Button */}
                        {!isShowingRejectInput ? (
                          <SoftButton
                            color="error"
                            size="small"
                            iconOnly
                            circular
                            onClick={() => setShowRejectInput(request.id)}
                            disabled={grantingId === request.id || rejectingId === request.id}
                          >
                            <CancelIcon />
                          </SoftButton>
                        ) : (
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftButton
                              color="error"
                              size="small"
                              onClick={() => handleRejectRequest(request.id, request.comment || "")}
                              disabled={rejectingId === request.id}
                            >
                              {rejectingId === request.id ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                "Confirm Reject"
                              )}
                            </SoftButton>
                            <SoftButton
                              color="dark"
                              size="small"
                              variant="outlined"
                              onClick={cancelReject}
                            >
                              Cancel
                            </SoftButton>
                          </SoftBox>
                        )}
                      </SoftBox>
                    </SoftBox>

                    {/* Grant Comment Input */}
                    {isShowingGrantInput && (
                      <SoftBox mt={1}>
                        <SoftInput
                          placeholder="Enter admin comment for granting permission (optional)..."
                          value={adminComment}
                          onChange={(e) => setAdminComment(e.target.value)}
                          multiline
                          rows={2}
                          fullWidth
                        />
                      </SoftBox>
                    )}

                    {/* Reject Comment Input */}
                    {isShowingRejectInput && (
                      <SoftBox mt={1}>
                        <SoftInput
                          placeholder="Enter rejection reason (required)..."
                          value={rejectComment}
                          onChange={(e) => setRejectComment(e.target.value)}
                          multiline
                          rows={2}
                          fullWidth
                        />
                      </SoftBox>
                    )}

                    {/* Show user comment */}
                    {request.comment && (
                      <SoftBox
                        bgcolor="#f9f9f9"
                        p={2}
                        borderRadius="8px"
                        border="1px solid #ddd"
                        mt={1}
                      >
                        <SoftTypography variant="caption" color="text.secondary" fontStyle="italic">
                          💬 User Comment: {request.comment}
                        </SoftTypography>
                      </SoftBox>
                    )}

                    {/* Show admin comment if exists */}
                    {request.admin_comment && (
                      <SoftBox
                        bgcolor="#e8f5e8"
                        p={2}
                        borderRadius="8px"
                        border="1px solid #4caf50"
                        mt={1}
                      >
                        <SoftTypography variant="caption" color="success.main" fontStyle="italic">
                          👨‍💼 Admin Comment: {request.admin_comment}
                        </SoftTypography>
                      </SoftBox>
                    )}
                  </SoftBox>
                );
              })
            )}
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Snackbar Notification */}
      <SoftSnackbar
        color={snackbar.color}
        icon={<TaskAltIcon />}
        title="Notification"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        dateTime={new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        time={3000}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default AdminInterviewRequests;
